import React, { useContext, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import {
  LayerLoadingState,
  LayerLoadingStatus,
  LayerLoadingStatuses,
} from '../../../../../viewer/layer-loading-status';
import { ViewLayer } from '../../../../../viewer/view-settings';
import { viewerZIndices } from '../../../../../viewer/viewer-z-indices';
import { useFeatureFlag } from '../../../../hooks/use-feature-flag';
import { SkyviewContext } from '../../../skyview/skyview-state';

/** How long a layer that is done stays before it fades out, and how long the fade is, in ms. */
export const doneVisibleMs = 1500;
export const fadeOutMs = 400;

/**
 * The timeline layers initializing, stacked at the top left by the asset menu, newest last. A layer
 * that is done fades out; one that failed stays until it's clicked.
 */
const LayerLoadingStatusStack = () => {
  const enabled = useFeatureFlag('SK-7549-layer-loading-status');
  const { activeScene } = useContext(SkyviewContext);
  const statuses = useSyncExternalStore(
    LayerLoadingStatuses.instance.subscribe,
    LayerLoadingStatuses.instance.getSnapshot,
  );

  // Without the flag nothing dismisses them, so they don't pile up for when it's turned on
  React.useEffect(() => {
    if (!enabled && statuses.length > 0) {
      LayerLoadingStatuses.instance.clear();
    }
  }, [enabled, statuses]);

  if (!enabled || activeScene === 'panoramaViewer' || statuses.length === 0) {
    return null;
  }

  return (
    <Component>
      {statuses.map((status) => (
        <StatusMessage key={status.id} status={status} />
      ))}
    </Component>
  );
};

/** The message of each layer, by its state. */
const useMessages = (): Record<LayerLoadingState, Record<ViewLayer, string>> => {
  const { t } = useTranslation();
  return {
    loading: {
      orthophoto: t('layerLoadingStatus.loading.orthophoto', { ns: 'skyview' }),
      tiledModel: t('layerLoadingStatus.loading.tiledModel', { ns: 'skyview' }),
      pointCloud: t('layerLoadingStatus.loading.pointCloud', { ns: 'skyview' }),
    },
    done: {
      orthophoto: t('layerLoadingStatus.done.orthophoto', { ns: 'skyview' }),
      tiledModel: t('layerLoadingStatus.done.tiledModel', { ns: 'skyview' }),
      pointCloud: t('layerLoadingStatus.done.pointCloud', { ns: 'skyview' }),
    },
    failed: {
      orthophoto: t('layerLoadingStatus.failed.orthophoto', { ns: 'skyview' }),
      tiledModel: t('layerLoadingStatus.failed.tiledModel', { ns: 'skyview' }),
      pointCloud: t('layerLoadingStatus.failed.pointCloud', { ns: 'skyview' }),
    },
  };
};

const StatusMessage = ({ status }: { status: LayerLoadingStatus }) => {
  const { t } = useTranslation();
  const message = useMessages()[status.state][status.layer];
  const dismiss = () => LayerLoadingStatuses.instance.dismiss(status.id);

  if (status.state === 'failed') {
    return (
      <Message
        as="button"
        state={status.state}
        title={t('layerLoadingStatus.dismiss', { ns: 'skyview' })}
        type="button"
        onClick={dismiss}
      >
        <Dot state={status.state} />
        {message}
      </Message>
    );
  }

  return (
    <Message
      role="status"
      state={status.state}
      onAnimationEnd={(event) => {
        // Only the fade out, not the dot's pulse
        if (status.state === 'done' && event.target === event.currentTarget) {
          dismiss();
        }
      }}
    >
      <Dot state={status.state} />
      {message}
    </Message>
  );
};

const fadeOut = keyframes`
  to {
    opacity: 0;
  }
`;

const pulse = keyframes`
  50% {
    opacity: 0.3;
  }
`;

const dotColors: Record<LayerLoadingState, string> = {
  loading: '#a5acaf',
  done: '#4caf50',
  failed: '#e53935',
};

// Right of the asset menu, which it follows as it collapses. Only the messages take clicks.
const Component = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: ${viewerZIndices.toolbar};
  pointer-events: none;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const Message = styled.div<{ state: LayerLoadingState }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  pointer-events: auto;

  font-family: 'Montserrat';
  font-size: 12px;
  white-space: nowrap;
  color: #37424a;
  background-color: #fff;
  border: none;
  border-radius: 14px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);

  ${(props) =>
    props.state === 'done' &&
    css`
      animation: ${fadeOut} ${fadeOutMs}ms ease-in ${doneVisibleMs}ms forwards;
    `}

  ${(props) =>
    props.state === 'failed' &&
    css`
      color: ${dotColors.failed};
      cursor: pointer;
    `}
`;

const Dot = styled.span<{ state: LayerLoadingState }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => dotColors[props.state]};

  ${(props) =>
    props.state === 'loading' &&
    css`
      animation: ${pulse} 1.2s ease-in-out infinite;
    `}
`;

export { LayerLoadingStatusStack };
